import { ref, computed } from 'vue';
import { defineStore } from 'pinia';
import { useAppSnackbar } from '@/stores/app/snackbar';
import {
  fetchApprovalTemplates,
  fetchApprovalFlowsByDraft,
  fetchMyPendingApprovals,
  createApprovalFlow,
  cancelApprovalFlow,
  addApprovalFlowApprovers,
} from '@/api/approvalFlows';
import {
  buildSlotAssignmentsFromTemplate,
  buildSlotsFromTemplate,
  getActiveFlow,
  getNewApproverIds,
} from '@/helpers/approvalFlows';

export const useApprovalFlows = defineStore('approvalFlows', () => {
  const templates = ref([]);
  const flows = ref([]);
  const myPending = ref([]);
  const selectedTemplateUlid = ref(null);
  const createStep = ref('template');
  const activeFlowUlid = ref(null);
  const initialShowStartedBanner = ref(false);
  const slotAssignments = ref([]);
  const editApprovalSlotSelectedUsers = ref({});
  const isLoading = ref(false);
  const isCreating = ref(false);
  const isCancelling = ref(false);
  const isSavingApprovers = ref(false);
  const error = ref(null);

  const activeTemplates = computed(() => (
    templates.value.filter((template) => !template.is_archived)
  ));

  const selectedTemplate = computed(() => (
    activeTemplates.value.find((template) => template.ulid === selectedTemplateUlid.value) ?? null
  ));

  const activeFlow = computed(() => (
    getActiveFlow(flows.value, activeFlowUlid.value)
  ));

  const canSubmitCreateFlow = computed(() => (
    slotAssignments.value.length > 0
    && slotAssignments.value.every((assignment) => assignment.selectedUsers?.length >= 1)
  ));

  const getEditableApprovalSlots = (flow) => (
    (flow?.stages ?? []).flatMap((stage) => (
      (stage.slots ?? []).filter((approvalSlot) => (
        approvalSlot.status === 'pending' || approvalSlot.status === 'staged'
      ))
    ))
  );

  const canSubmitEditFlow = computed(() => (
    getEditableApprovalSlots(activeFlow.value).some((approvalSlot) => {
      const selectedUsers = editApprovalSlotSelectedUsers.value[approvalSlot.ulid] ?? [];

      return getNewApproverIds(approvalSlot, selectedUsers).length > 0;
    })
  ));

  const hasActiveApprovalFlow = computed(() => (
    flows.value.some((flow) => flow.is_current && flow.status === 'pending')
  ));

  const upsertFlow = (flow) => {
    if (!flow?.ulid) return;

    const index = flows.value.findIndex(({ ulid }) => ulid === flow.ulid);

    if (index === -1) {
      flows.value = [flow, ...flows.value];
      return;
    }

    flows.value[index] = { ...flows.value[index], ...flow };
  };

  const fetchTemplates = async () => {
    const data = await fetchApprovalTemplates();
    templates.value = data.templates;
  };

  const fetchFlows = async (draftUlid) => {
    if (!draftUlid) {
      flows.value = [];
      return;
    }

    const data = await fetchApprovalFlowsByDraft({ draftUlid });
    flows.value = data.flows || [];
  };

  const fetchMyPending = async () => {
    const data = await fetchMyPendingApprovals();
    myPending.value = data?.items || [];
  };

  const loadMyPending = async () => {
    isLoading.value = true;

    try {
      await fetchMyPending();
    } catch (err) {
      useAppSnackbar().addErrorSnackbar({
        text: 'Could not load My Pending Approvals',
      });
      throw err;
    } finally {
      isLoading.value = false;
    }
  };

  const loadPanelData = async (draftUlid) => {
    isLoading.value = true;
    error.value = null;

    try {
      await Promise.all([
        fetchTemplates(),
        fetchFlows(draftUlid),
      ]);
    } catch (err) {
      templates.value = [];
      flows.value = [];
      error.value = err.message;

      throw err;
    } finally {
      isLoading.value = false;
    }
  };

  const beginAssignApprovers = () => {
    const template = selectedTemplate.value;

    if (!template) {
      throw new Error('Select an approval template');
    }

    const assignments = buildSlotAssignmentsFromTemplate(template);

    if (!assignments.length) {
      throw new Error('Selected template has no approval slots');
    }

    slotAssignments.value = assignments;
    createStep.value = 'assign';
  };

  const backToTemplateSelect = () => {
    createStep.value = 'template';
    slotAssignments.value = [];
  };

  const openFlowDetail = (flowUlid, { showBanner = false } = {}) => {
    activeFlowUlid.value = flowUlid;
    createStep.value = 'detail';

    initialShowStartedBanner.value = showBanner;
  };

  const openFlowForEdit = (flowUlid) => {
    const flow = getActiveFlow(flows.value, flowUlid);

    if (!flow || flow.status !== 'pending') return false;

    activeFlowUlid.value = flowUlid;
    editApprovalSlotSelectedUsers.value = getEditableApprovalSlots(flow).reduce(
      (selectedUsersByApprovalSlot, approvalSlot) => ({
        ...selectedUsersByApprovalSlot,
        [approvalSlot.ulid]: [],
      }),
      {},
    );
    createStep.value = 'edit';

    return true;
  };

  const openFlowEdit = () => {
    openFlowForEdit(activeFlowUlid.value);
  };

  const backToFlowDetail = () => {
    editApprovalSlotSelectedUsers.value = {};
    createStep.value = 'detail';
  };

  const viewOtherApprovals = () => {
    activeFlowUlid.value = null;
    initialShowStartedBanner.value = false;
    createStep.value = 'template';
    slotAssignments.value = [];
    editApprovalSlotSelectedUsers.value = {};
    selectedTemplateUlid.value = null;
  };

  const clearStartedBanner = () => {
    initialShowStartedBanner.value = false;
  };

  const updateApprovalSlotApprovers = (approvalSlotIndex, selectedUsers) => {
    if (approvalSlotIndex < 0 || approvalSlotIndex >= slotAssignments.value.length) {
      return;
    }

    slotAssignments.value[approvalSlotIndex] = {
      ...slotAssignments.value[approvalSlotIndex],
      selectedUsers,
    };
  };

  const updateEditApprovalSlotApprovers = (approvalSlotUlid, selectedUsers) => {
    editApprovalSlotSelectedUsers.value = {
      ...editApprovalSlotSelectedUsers.value,
      [approvalSlotUlid]: selectedUsers,
    };
  };

  const createFlow = async ({ draftUlid }) => {
    const template = selectedTemplate.value;

    if (!template || !draftUlid) {
      throw new Error('Template and draft profile are required');
    }

    const slots = buildSlotsFromTemplate(slotAssignments.value);

    if (!slots.length) {
      throw new Error('Selected template has no approval slots');
    }

    isCreating.value = true;
    error.value = null;

    try {
      const flow = await createApprovalFlow({
        templateUlid: template.ulid,
        draftJobProfileUlid: draftUlid,
        slots,
      });

      upsertFlow(flow);

      slotAssignments.value = [];
      selectedTemplateUlid.value = null;

      openFlowDetail(flow.ulid, { showBanner: true });
    } catch (err) {
      error.value = err.message;

      throw err;
    } finally {
      isCreating.value = false;
    }
  };

  const saveAddedApprovers = async () => {
    const flow = activeFlow.value;

    if (!flow || flow.status !== 'pending') {
      throw new Error('No pending approval flow selected');
    }

    const approvalSlotUpdates = getEditableApprovalSlots(flow)
      .map((approvalSlot) => {
        const selectedUsers = editApprovalSlotSelectedUsers.value[approvalSlot.ulid] ?? [];

        return {
          approvalSlot,
          newApproverIds: getNewApproverIds(approvalSlot, selectedUsers),
        };
      })
      .filter(({ newApproverIds }) => newApproverIds.length > 0);

    isSavingApprovers.value = true;
    error.value = null;

    try {
      await approvalSlotUpdates.reduce(async (previousPromise, { approvalSlot, newApproverIds }) => {
        await previousPromise;

        const updatedFlow = await addApprovalFlowApprovers({
          approvalFlowSlotUlid: approvalSlot.ulid,
          userUlids: newApproverIds,
          approvalFlowUlid: flow.ulid,
        });

        upsertFlow(updatedFlow);
      }, Promise.resolve());

      backToFlowDetail();
    } catch (err) {
      error.value = err.message;

      throw err;
    } finally {
      isSavingApprovers.value = false;
    }
  };

  const cancelActiveFlow = async () => {
    if (!activeFlowUlid.value) {
      throw new Error('No active approval flow selected');
    }

    isCancelling.value = true;
    error.value = null;

    try {
      const draftUlid = activeFlow.value?.draft_job_profile_ulid;

      await cancelApprovalFlow({ flowUlid: activeFlowUlid.value });

      if (draftUlid) {
        await fetchFlows(draftUlid);
      }

      viewOtherApprovals();
    } catch (err) {
      error.value = err.message;

      throw err;
    } finally {
      isCancelling.value = false;
    }
  };

  const reset = () => {
    templates.value = [];
    flows.value = [];
    selectedTemplateUlid.value = null;
    createStep.value = 'template';
    activeFlowUlid.value = null;
    initialShowStartedBanner.value = false;
    slotAssignments.value = [];
    editApprovalSlotSelectedUsers.value = {};
    isLoading.value = false;
    isCreating.value = false;
    isCancelling.value = false;
    isSavingApprovers.value = false;
    error.value = null;
  };

  return {
    templates,
    flows,
    myPending,
    selectedTemplateUlid,
    createStep,
    activeFlowUlid,
    initialShowStartedBanner,
    slotAssignments,
    editApprovalSlotSelectedUsers,
    isLoading,
    isCreating,
    isCancelling,
    isSavingApprovers,
    error,
    activeTemplates,
    selectedTemplate,
    activeFlow,
    canSubmitCreateFlow,
    canSubmitEditFlow,
    hasActiveApprovalFlow,
    fetchTemplates,
    fetchFlows,
    fetchMyPending,
    loadPanelData,
    loadMyPending,
    beginAssignApprovers,
    backToTemplateSelect,
    openFlowDetail,
    openFlowForEdit,
    openFlowEdit,
    backToFlowDetail,
    viewOtherApprovals,
    clearStartedBanner,
    updateApprovalSlotApprovers,
    updateEditApprovalSlotApprovers,
    createFlow,
    saveAddedApprovers,
    cancelActiveFlow,
    reset,
  };
});
